import { useState, useEffect, useCallback } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useMutation, useLazyQuery } from "@apollo/client/react";
import { UPDATE_PERSONAL_ROOM } from "@/graphql/mutations";
import { CHECK_ROOM_NAME_AVAILABILITY } from "@/graphql/queries";
import { useToast } from "@/hooks/use-toast";


interface PersonalRoom {
    id: string;
    title?: string;
    description?: string;
    roomName?: string;
}

interface EditPersonalRoomDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    room: PersonalRoom;
    onSuccess?: () => void;
}

export const EditPersonalRoomDialog = ({ open, onOpenChange, room, onSuccess }: EditPersonalRoomDialogProps) => {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [roomName, setRoomName] = useState("");
    const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
    const [isChecking, setIsChecking] = useState(false);
    const { toast } = useToast();

    const [checkAvailabilityQuery] = useLazyQuery(CHECK_ROOM_NAME_AVAILABILITY, {
        fetchPolicy: "network-only",
    });

    const checkNameAvailability = useCallback((name: string) => {
        checkAvailabilityQuery({ variables: { roomName: name } })
            .then((result) => {
                if (result.data) {
                    setIsAvailable((result.data as { checkRoomNameAvailability: boolean }).checkRoomNameAvailability);
                } else {
                    setIsAvailable(false);
                }
                setIsChecking(false);
            })
            .catch(() => {
                setIsAvailable(false);
                setIsChecking(false);
            });
    }, [checkAvailabilityQuery]);

    // Initialize fields when dialog opens
    useEffect(() => {
        if (open && room) {
            setTitle(room.title || "");
            setDescription(room.description || "");
            setRoomName(room.roomName || "");
            setIsAvailable(null);
        }
    }, [open, room]);

    // Debounced URL availability check when roomName changes
    useEffect(() => {
        if (!open) return;
        if (!roomName) return;
        setIsChecking(true);
        setIsAvailable(null);
        const timer = setTimeout(() => {
            if (roomName !== room.roomName) {
                checkNameAvailability(roomName);
            } else {
                setIsAvailable(true);
                setIsChecking(false);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [roomName, open, checkNameAvailability, room]);

    const [updateRoom, { loading }] = useMutation(UPDATE_PERSONAL_ROOM);

    const handleUpdate = async () => {
        if (!title.trim()) {
            toast({
                title: "Error",
                description: "Room title is required.",
                variant: "destructive",
            });
            return;
        }
        if (isAvailable === false) {
            toast({
                title: "Error",
                description: "Custom URL is already taken.",
                variant: "destructive",
            });
            return;
        }
        try {
            await updateRoom({
                variables: {
                    input: {
                        roomId: room.id,
                        title: title.trim(),
                        ...(description && { description }),
                        ...(roomName && { roomName: roomName.trim() }),
                    },
                },
            });
            toast({
                title: "Room Updated",
                description: "Your personal room details have been updated.",
            });
            if (onSuccess) onSuccess();
        } catch (error) {
            toast({
                title: "Error",
                description: (error as Error).message || "Failed to update room.",
                variant: "destructive",
            });
        }
    };

    const handleClose = () => {
        setTitle("");
        setDescription("");
        setRoomName("");
        setIsAvailable(null);
        setIsChecking(false);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-lg bg-card border-border">
                <DialogHeader>
                    <DialogTitle className="text-2xl">Edit Personal Room</DialogTitle>
                    <DialogDescription>
                        Update the details of your personal room. Changes are saved immediately.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="title">Room Title</Label>
                        <Input
                            id="title"
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="bg-background border-border"
                            placeholder="e.g., Client Meetings, Design Reviews"
                        />
                        <p className="text-xs text-muted-foreground">
                            Visible only to you. You can edit this later.
                        </p>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="roomName">Custom URL</Label>
                        <div className="relative">
                            <div className="flex items-center">
                                <span className="bg-muted px-3 py-2 border border-r-0 border-border rounded-l-md text-muted-foreground text-sm whitespace-nowrap">
                                    meet.flowlabs.live/
                                </span>
                                <Input
                                    id="roomName"
                                    type="text"
                                    value={roomName}
                                    onChange={(e) => {
                                        const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
                                        setRoomName(val);
                                    }}
                                    className={`bg-background border-border rounded-l-none pr-10 ${isAvailable === true ? "border-green-500 focus-visible:ring-green-500" :
                                        isAvailable === false ? "border-red-500 focus-visible:ring-red-500" : ""
                                        }`}
                                    placeholder="my-custom-room"
                                />
                            </div>
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                {isChecking ? (
                                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                ) : isAvailable === true ? (
                                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                                ) : isAvailable === false ? (
                                    <XCircle className="h-4 w-4 text-red-500" />
                                ) : null}
                            </div>
                        </div>
                        <div className="flex justify-between text-xs">
                            <p className="text-muted-foreground">Only letters, numbers, and hyphens allowed.</p>
                            {isAvailable === false && !isChecking && (
                                <p className="text-red-500 font-medium">This URL is already taken.</p>
                            )}
                            {isAvailable === true && !isChecking && (
                                <p className="text-green-500 font-medium">Available!</p>
                            )}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="description">Description (Optional)</Label>
                        <Textarea
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="bg-background border-border min-h-[80px]"
                            placeholder="Brief description of this room's purpose"
                        />
                        <p className="text-xs text-muted-foreground">
                            Visible only to you.
                        </p>
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpdate}
                        disabled={loading || isChecking}
                        className="bg-gradient-hero text-white hover:opacity-90 font-semibold"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Updating...
                            </>
                        ) : (
                            "Save Changes"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
